import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ReturnDepositModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onConfirm: (grossAmount: number, notes?: string) => Promise<void>;
	isSubmitting: boolean;
	cardKey?: string | null;
	/** Original deposit amount to pre-fill, if available */
	depositAmount?: number | null;
}

export const ReturnDepositModal = ({
	open,
	onOpenChange,
	onConfirm,
	isSubmitting,
	cardKey,
	depositAmount,
}: ReturnDepositModalProps) => {
	const { t: tC } = useTranslation('contracts');
	const [amount, setAmount] = useState(
		depositAmount != null ? String(depositAmount) : ''
	);
	const [notes, setNotes] = useState('');

	const parsedAmount = Number(amount);
	const isValid = amount.trim() !== '' && !Number.isNaN(parsedAmount) && parsedAmount > 0;
	const exceedsDeposit = depositAmount != null && isValid && parsedAmount > depositAmount;

	const handleSubmit = async () => {
		if (!isValid || exceedsDeposit) return;
		await onConfirm(parsedAmount, notes || undefined);
		setAmount('');
		setNotes('');
	};

	const handleOpenChange = (nextOpen: boolean) => {
		if (!nextOpen) {
			setAmount(depositAmount != null ? String(depositAmount) : '');
			setNotes('');
		}
		onOpenChange(nextOpen);
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent size="sm">
				<DialogHeader>
					<DialogTitle>{tC('guarantees.returnDepositTitle')}</DialogTitle>
					<DialogDescription>
						{tC('guarantees.returnDepositDescription', { cardKey: cardKey ?? '—' })}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-2">
					<div className="space-y-2">
						<Label htmlFor="return-amount">
							{tC('guarantees.amount')}
						</Label>
						<Input
							id="return-amount"
							type="number"
							min="0.01"
							step="0.01"
							max={depositAmount ?? undefined}
							placeholder="0.00"
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
							autoFocus
						/>
						{exceedsDeposit && (
							<p className="text-sm text-destructive">
								{tC('guarantees.amountExceedsDeposit', { max: depositAmount })}
							</p>
						)}
					</div>
					<div className="space-y-2">
						<Label htmlFor="return-notes">
							{tC('guarantees.notes')}
						</Label>
						<Textarea
							id="return-notes"
							placeholder={tC('guarantees.notesPlaceholder')}
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
							rows={2}
						/>
					</div>
				</div>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => handleOpenChange(false)}
						disabled={isSubmitting}
					>
						{tC('guarantees.cancel')}
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={!isValid || exceedsDeposit || isSubmitting}
					>
						{isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
						{tC('guarantees.returnDeposit')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
